/**
 * ShareLoadPage — public, no-auth load tracking for customers.
 *
 * Resolves /share/:shareToken via the public backend endpoint and shows the
 * sanitized load snapshot: status, pickup → delivery, ETA, last GPS ping.
 * Auto-refreshes every 60 sec so an open browser tab stays current.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { API } from '../backend';

const STATUS_PILL = {
  'New':              { color: '#475569', bg: '#f1f5f9', label: 'Scheduled' },
  'Assigned':         { color: '#1e3a8a', bg: '#dbeafe', label: 'Driver assigned' },
  'Confirmed':        { color: '#1e3a8a', bg: '#dbeafe', label: 'Confirmed' },
  'Waiting':          { color: '#92400e', bg: '#fef3c7', label: 'At pickup' },
  'Loading':          { color: '#92400e', bg: '#fef3c7', label: 'Loading' },
  'In Progress':      { color: '#065f46', bg: '#d1fae5', label: 'En route' },
  'Waiting at drop':  { color: '#92400e', bg: '#fef3c7', label: 'At delivery' },
  'Unloading':        { color: '#92400e', bg: '#fef3c7', label: 'Unloading' },
  'Completed':        { color: '#065f46', bg: '#d1fae5', label: 'Delivered' },
  'Approved':         { color: '#065f46', bg: '#d1fae5', label: 'Delivered' },
  'Needs Review':     { color: '#92400e', bg: '#fef3c7', label: 'Finalizing' },
  'Cancelled':        { color: '#991b1b', bg: '#fee2e2', label: 'Cancelled' },
};

function fmtTimeUntil(iso) {
  if (!iso) return '—';
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return '—';
  const absMin = Math.round(Math.abs(ms) / 60000);
  const txt = absMin < 60 ? `${absMin}m`
            : absMin < 1440 ? `${Math.floor(absMin / 60)}h ${absMin % 60}m`
            : `${Math.floor(absMin / 1440)}d ${Math.floor((absMin % 1440) / 60)}h`;
  return ms < 0 ? `${txt} ago` : `in ${txt}`;
}

function fmtClock(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function ShareLoadPage() {
  const { shareToken } = useParams();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/share/load/${encodeURIComponent(shareToken)}`);
      const json = await res.json();
      if (!res.ok || json.error) {
        setError(json.error || 'Tracking link not found');
        return;
      }
      setError(null);
      setData(json);
      setUpdatedAt(new Date());
    } catch (e) {
      setError('Could not reach the tracking service');
    } finally {
      setLoading(false);
    }
  }, [shareToken]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  const wrap = {
    minHeight: '100vh',
    padding: '32px 16px',
    background: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  };
  const card = {
    maxWidth: 540, margin: '0 auto',
    background: 'white', borderRadius: 14,
    boxShadow: '0 10px 35px rgba(0,0,0,0.08)',
    padding: '24px 22px',
  };

  if (loading) return (
    <div style={wrap}><div style={{ ...card, textAlign: 'center', color: '#64748b' }}>Loading tracking…</div></div>
  );
  if (error) return (
    <div style={wrap}>
      <div style={{ ...card, textAlign: 'center' }}>
        <div style={{ fontSize: 22, marginBottom: 8 }}>🔗</div>
        <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 16 }}>{error}</div>
        <div style={{ marginTop: 12, fontSize: 13, color: '#64748b' }}>
          Please contact your dispatcher for an updated link.
        </div>
      </div>
    </div>
  );

  const pill = STATUS_PILL[data.status] || { color: '#475569', bg: '#f1f5f9', label: data.status || '—' };
  const isComplete = ['Completed', 'Approved'].includes(data.status);
  const eta = isComplete ? data.completedAt : data.etaArrivalAt;

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Load tracking
          </div>
          <span style={{
            background: pill.bg, color: pill.color,
            padding: '4px 10px', borderRadius: 14,
            fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}>{pill.label}</span>
        </div>

        <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em' }}>
          {data.dispatchId}
        </div>
        {data.customer && (
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{data.customer}</div>
        )}

        {/* Route */}
        <div style={{ marginTop: 22, marginBottom: 22, paddingLeft: 4 }}>
          <div style={{ display: 'flex', alignItems: 'start', gap: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 5 }}>
              <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#16a34a', boxShadow: '0 0 0 3px #dcfce7' }} />
              <span style={{ width: 2, height: 38, background: '#cbd5e1', margin: '4px 0' }} />
              <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#dc2626', boxShadow: '0 0 0 3px #fee2e2' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Pickup</div>
              <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 600, marginTop: 1 }}>{data.pickup || '—'}</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                {data.pickupArrivedAt ? `Arrived ${fmtClock(data.pickupArrivedAt)}` : (data.pickupDate ? `Scheduled ${new Date(data.pickupDate).toLocaleDateString(undefined, { timeZone: 'UTC' })}${data.pickupTime ? ' at ' + data.pickupTime : ''}` : '—')}
              </div>

              <div style={{ marginTop: 20, fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Delivery</div>
              <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 600, marginTop: 1 }}>{data.delivery || '—'}</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                {data.deliveryArrivedAt ? `Arrived ${fmtClock(data.deliveryArrivedAt)}` : 'Pending'}
              </div>
            </div>
          </div>
        </div>

        {/* ETA */}
        {eta && (
          <div style={{
            background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10,
            padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {isComplete ? 'Delivered' : 'Estimated arrival'}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginTop: 2 }}>
                {fmtClock(eta)}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, color: '#1d4ed8', fontWeight: 700 }}>
                {fmtTimeUntil(eta)}
              </div>
            </div>
          </div>
        )}

        {/* Live location */}
        {data.liveTruck && (
          <div style={{
            marginTop: 14, background: '#f8fafc', borderRadius: 10,
            padding: '12px 14px', fontSize: 12, color: '#475569',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontWeight: 700, color: '#0f172a' }}>📍 Current location</span>
              <span style={{ fontSize: 10, color: '#94a3b8' }}>
                Updated {fmtTimeUntil(data.liveTruck.recordedAt)}
              </span>
            </div>
            <div>{data.liveTruck.address || `${data.liveTruck.lat.toFixed(4)}, ${data.liveTruck.lng.toFixed(4)}`}</div>
            {data.liveTruck.speedKmh != null && data.liveTruck.speedKmh > 5 && (
              <div style={{ marginTop: 4, color: '#1d4ed8' }}>{Math.round(data.liveTruck.speedKmh)} km/h</div>
            )}
          </div>
        )}

        <div style={{ marginTop: 18, fontSize: 10, color: '#94a3b8', textAlign: 'center' }}>
          Auto-refreshes every minute
          {updatedAt && <> · last checked {updatedAt.toLocaleTimeString()}</>}
        </div>
      </div>
    </div>
  );
}
